import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ModalShell from "../../../../_components/modal/ModalShell";
import { TextInput } from "../../../../_components/ui";
import { useCreateOrganisationMutation } from "../../../accountSettings/api/organisationApiSlice";
import { useChangeOrganisation } from "../../hooks/useChangeOrganisation";
import { useLazyGetAllObjectCustomizationsQuery } from "../../../objectCustomization/api/objectCustomizationApi";
import { setObjectCustomizations } from "../../../objectCustomization/state/slice";
import { useLazyGetAllPlatformSettingsQuery } from "../../../platformSettings/api/platformSettingsApiSlice";
import { permissionApiSlice } from "../../../permissions/api/permissionApiSlice";
import { paths } from "../../../../_app/router/paths";
import { useToast } from "../../../../_context/ToastContext/ToastContext";
import "./_create-organisation-modal.scss";

/**
 * Collects org details, creates the Organisation (server-side makes the
 * caller its owner and org admin - see organisation.controller.js), then
 * activates it exactly the way useOrganisationSelection does after a
 * switch: changeOrganisation to mint a JWT with the new org as
 * `selectedOrg`, warm object-customization/platform-settings caches, force
 * a fresh permissions fetch (the changeOrg response doesn't embed them), and
 * land on /onboarding - a brand-new org is always owner + not onboarded.
 */
const CreateOrganisationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [createOrganisation, { isLoading: isCreating }] = useCreateOrganisationMutation();
  const { changeOrganisation, isChangingOrg } = useChangeOrganisation();
  const [getAllObjectCustomizations] = useLazyGetAllObjectCustomizationsQuery();
  const [getAllPlatformSettings] = useLazyGetAllPlatformSettingsQuery();

  const isSubmitting = isCreating || isChangingOrg;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { orgName: "", orgShortName: "" },
    mode: "onBlur",
  });

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const { data: newOrg } = await createOrganisation({
        orgName: values.orgName,
        orgShortName: values.orgShortName || undefined,
      }).unwrap();

      await changeOrganisation(newOrg._id, {
        onSuccess: async () => {
          const objectCustomizations = await getAllObjectCustomizations().unwrap();
          dispatch(setObjectCustomizations(objectCustomizations));
          getAllPlatformSettings().catch(() => {});
          await dispatch(
            permissionApiSlice.endpoints.getCurrentUserPermissions.initiate(undefined, {
              forceRefetch: true,
            })
          ).unwrap().catch(() => {});
        },
      });

      reset();
      onClose?.();
      navigate(paths.onboarding, { state: { orgId: newOrg._id } });
    } catch (error) {
      showToast(error?.data?.message || "Failed to create organisation. Please try again.", "error");
    }
  });

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen
      onRequestClose={handleClose}
      title="New organisation"
      width="400px"
      isSubmitting={isSubmitting}
      footerButtons={[
        { children: "Cancel", variant: "outlined", onClick: handleClose, disabled: isSubmitting },
        { children: "Create", variant: "contained", onClick: onSubmit, loading: isSubmitting },
      ]}
    >
      <div className="create-organisation-modal__fields">
        <Controller
          name="orgName"
          control={control}
          rules={{ required: "Organisation name is required." }}
          render={({ field, fieldState }) => (
            <TextInput
              id="orgName"
              label="Organisation name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              disabled={isSubmitting}
              required
            />
          )}
        />
        <Controller
          name="orgShortName"
          control={control}
          render={({ field }) => (
            <TextInput
              id="orgShortName"
              label="Short name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isSubmitting}
            />
          )}
        />
      </div>
    </ModalShell>
  );
};

export default CreateOrganisationModal;
